import React, { useState } from 'react';
import { GivelifyIcon } from '../../icon';
import { GivelifyLabel } from '../../label';
import { useImageEditorContext } from '../context/ImageEditorContext';
import {
    BackgroundControls,
    BackgroundOption,
    BackgroundOptionContainer,
    BackgroundOptions,
    Controls,
    ControlsRow,
    IconButton,
    OptionBlur,
    OptionColor,
    RotateControls,
    StyledTooltip,
    ZoomControls,
    ZoomRow,
    ZoomSlider,
} from '../style';
import type { BackgroundOptionItem } from '../types';
import type { BackgroundSelection, PreparedImage } from '../utils/convertImage';

export type ControlsPanelProps = {
    sliderValue: number;
    onZoomChange: (value: number) => void;
    onZoomMinus: () => void;
    onZoomPlus: () => void;
    onRotate: () => void;
    backgroundOptions: BackgroundOptionItem[];
    backgroundSelection: BackgroundSelection;
    hideBackgroundSelection?: boolean;
    isBackgroundVisible: boolean;
    onBackgroundChange: (value: BackgroundSelection, index: number) => void;
    imageData: PreparedImage | null;
};

export const ControlsPanel: React.FC<ControlsPanelProps> = ({
    sliderValue,
    onZoomChange,
    onZoomMinus,
    onZoomPlus,
    onRotate,
    backgroundOptions,
    backgroundSelection,
    hideBackgroundSelection = false,
    isBackgroundVisible,
    onBackgroundChange,
    imageData,
}) => {
    const { copy, name } = useImageEditorContext();
    const [activeTooltip, setActiveTooltip] = useState<number>();

    return (
        <Controls>
            <ControlsRow hideBackgroundSelection={hideBackgroundSelection}>
                <ZoomControls>
                    <GivelifyLabel
                        className="control-label"
                        text={copy.zoom}
                        variant="body2"
                    />
                    <ZoomRow>
                        <IconButton id={`${name}-zoom-minus`} onClick={onZoomMinus} variant="icon">
                            <GivelifyIcon variant="minus" />
                        </IconButton>
                        <ZoomSlider
                            aria-label={copy.zoom}
                            id={`${name}-zoom-slider`}
                            max={1}
                            min={0}
                            onChange={(_event, value: number | number[]) => {
                                const sliderVal =
                                    typeof value === 'number'
                                        ? value
                                        : value[0];
                                onZoomChange(sliderVal);
                            }}
                            step={0.001}
                            value={sliderValue}
                        />
                        <IconButton id={`${name}-zoom-plus`} onClick={onZoomPlus} variant="icon">
                            <GivelifyIcon variant="plus" />
                        </IconButton>
                    </ZoomRow>
                </ZoomControls>
                <RotateControls>
                    <GivelifyLabel
                        className="control-label"
                        text={copy.rotate}
                        variant="body2"
                    />
                    <IconButton id={`${name}-rotate`} onClick={onRotate} variant="icon">
                        <GivelifyIcon fontSize={28} variant="rotate-right" />
                    </IconButton>
                </RotateControls>
                {hideBackgroundSelection ? null : (
                    <BackgroundControls>
                        <GivelifyLabel
                            className="control-label"
                            text={copy.background}
                            variant="body2"
                        />
                        <BackgroundOptions>
                            {backgroundOptions.map((option, index) => {
                                const isSelected =
                                    option.value.type ===
                                        backgroundSelection.type &&
                                    (option.value.type !== 'color' ||
                                        (backgroundSelection.type === 'color' &&
                                            option.value.color ===
                                                backgroundSelection.color));
                                const disabled = !isBackgroundVisible;
                                return (
                                    <StyledTooltip
                                        key={option.id}
                                        arrow
                                        disableHoverListener={!disabled}
                                        onClose={() =>
                                            setActiveTooltip(undefined)
                                        }
                                        open={activeTooltip === index}
                                        placement="top"
                                        title={
                                            disabled ? copy.zoomDisabled : ''
                                        }
                                    >
                                        <BackgroundOption
                                            disableRipple
                                            isDisabled={disabled}
                                            isSelected={isSelected}
                                            onClick={() => {
                                                onBackgroundChange(
                                                    option.value,
                                                    index,
                                                );
                                                if (disabled) {
                                                    setActiveTooltip(index);
                                                }
                                            }}
                                            variant="icon"
                                        >
                                            <BackgroundOptionContainer>
                                                {option.value.type ===
                                                'blur' ? (
                                                    <OptionBlur
                                                        style={{
                                                            backgroundImage:
                                                                imageData?.src
                                                                    ? `url(${imageData.src})`
                                                                    : undefined,
                                                        }}
                                                    />
                                                ) : (
                                                    <OptionColor
                                                        style={{
                                                            backgroundColor:
                                                                option.value
                                                                    .color,
                                                        }}
                                                    />
                                                )}
                                            </BackgroundOptionContainer>
                                        </BackgroundOption>
                                    </StyledTooltip>
                                );
                            })}
                        </BackgroundOptions>
                    </BackgroundControls>
                )}
            </ControlsRow>
        </Controls>
    );
};
